import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { sql } from '@codemirror/lang-sql';
import { yaml } from '@codemirror/lang-yaml';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { StreamLanguage } from '@codemirror/language';
import { EditorState } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { computed, nextTick, ref, shallowRef, watch } from 'vue';
import { DEFAULT_CONFIG } from 'vue-codemirror';
import { useInternational } from '@/common/locale';
import type { CodeEditorOptions, EditorLanguage, EditorTheme } from './codeEditor.types';
import { addLineHighlight } from './extenstions/lineHighlight';

interface Emits {
  (e: 'update:code', value: string): void;
  (e: 'change', value: string): void;
  (e: 'copy', value: string): void;
}

interface Props {
  code: string;
  options?: CodeEditorOptions;
  isLoading?: boolean;
  placeholder?: string;
}

const setup = (props: Props, emits: Emits) => {
  const { t } = useInternational();
  const codeLoadingText = ref<string>('');
  const loadElHeight = ref<number>(0);
  const loadTargetLength = ref<number>(0);
  const codeText = computed<string>({
    get: () => codeLoadingText.value || props.code,
    set: (value) => {
      emits('update:code', value);
    },
  });

  const view = shallowRef<EditorView>();
  const wrapperRef = ref<HTMLDivElement | null>(null);
  const handleReady = (payload: {
    view: EditorView;
    state: EditorState;
    container: HTMLDivElement;
  }) => {
    view.value = payload.view;
  };

  const lineHighlight = (lineNumbers: number[]) => {
    if (!view.value) return;

    const pos = lineNumbers.map((lineNumber) => view.value!.state.doc.line(lineNumber).from);
    const effects = pos.map((docPosition) => addLineHighlight.of(docPosition));

    view.value.dispatch({ effects });
  };

  const scrollInto = (lineNumber: number) => {
    if (!view.value) return;

    view.value.dispatch({
      selection: {
        anchor: view.value.state.doc.line(lineNumber).from,
        head: view.value.state.doc.line(lineNumber).from,
      },
      scrollIntoView: true,
    });
  };

  const getFocusedLine = () => {
    if (!view.value) return 1;
    return view.value.state.doc.lineAt(view.value.state.selection.main.head).number;
  };

  watch(
    () => props.isLoading,
    async (val) => {
      if (val) {
        await nextTick();
        const wrapperEl = wrapperRef.value;
        if (wrapperEl) {
          const cmEl = wrapperEl?.querySelector('.cm-line');
          loadElHeight.value = cmEl?.getBoundingClientRect()?.height || 20;
          loadTargetLength.value = Math.floor((wrapperEl.clientHeight ?? 0) / loadElHeight.value);
          codeLoadingText.value = Array.from({
            length:
              loadTargetLength.value > 0 ? loadTargetLength.value - 1 : loadTargetLength.value,
          })
            .fill('\n')
            .join('');
        }
      } else {
        codeLoadingText.value = '';
      }
    },
    { immediate: true },
  );

  return {
    t,
    codeText,
    wrapperRef,
    loadElHeight,
    loadTargetLength,
    handleReady,
    lineHighlight,
    getFocusedLine,
    scrollInto,
  };
};

const extensionsForLanguage: Record<EditorLanguage, any> = {
  javascript,
  json,
  sql,
  yaml,
  shell: () => StreamLanguage.define(shell)?.extension ?? [],
};

const useCodeMirror = ({
  language,
  theme,
  options,
  extensions = [],
}: {
  language?: EditorLanguage;
  extensions?: any[];
  theme: EditorTheme;
  options: Omit<CodeEditorOptions, 'extensions'>;
}) => {
  const defaultExtensions = language ? [extensionsForLanguage[language]?.()] : [];
  const themeExtensions = theme === 'dark' ? [oneDark] : [];
  const readOnlyExtensions = options.readonly ? [EditorState.readOnly.of(true)] : [];
  const codeEditorOptions: CodeEditorOptions = {
    ...DEFAULT_CONFIG,
    showLineNumbers: true,
    showActiveCurrentLine: true,
    extensions: [...defaultExtensions, ...themeExtensions, ...readOnlyExtensions, ...extensions],
    ...options,
  };

  return { codeEditorOptions };
};

export { setup, useCodeMirror };
export type { Emits, Props };
